import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import heroSlide2 from '../../../images/home/hero-slide-2.png';
import heroSlide3 from '../../../images/home/hero-slide-3.png';
import heroSlide4 from '../../../images/home/hero-slide-4.png';
import { HomeBtn } from './HomePrimitives';
import { HOME_EASE } from './HomeMotion';
import { initBannerSwiper } from '../../../utils/swiperBannerInit';
import { usePageBanners } from '../../../hooks/usePageBanners';

const HOME_BANNER_SECTION = 'Home Page';

const FALLBACK_SLIDES = [
  {
    id: 'add-a-module',
    title: "Pay for what you use. Skip what you don't.",
    description:
      "Pick the tools you need, skip the ones you don't, and be up and running in minutes — no IT department required.",
    image: heroSlide2,
    alt: 'Add a module — start with Tasks and add Documents, Snap bill, Vault, Forum, or Projects anytime',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
  {
    id: 'affordable-pricing',
    title: 'Pay as low as $1 per month. 80% less than market rate.',
    description:
      'Our pricing is tailored to support small and medium enterprises. No additional application required to manage your business.',
    image: heroSlide3,
    alt: 'Affordable module pricing — pay only for the flowdo modules you use',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
  {
    id: 'free-forever',
    title: 'Free forever! No Credit Card.',
    description:
      'Every module starts free. Explore the platform risk-free, and upgrade only when you\'re ready for advanced features.',
    image: heroSlide4,
    alt: 'No credit card required — every flowdo module is free to start',
    buttonText: 'Get Started',
    buttonTo: '/register',
  },
];

const isExternalUrl = (url) => /^https?:\/\//i.test(url || '');

function mapBannerToSlide(banner) {
  return {
    id: banner.id ?? banner.name,
    title: banner.main_heading_text || '',
    description: banner.sub_heading_text || '',
    image: banner.imageDesktopUrl,
    imageMobile: banner.imageMobileUrl,
    alt: banner.name || banner.main_heading_text || 'flowdo banner',
    buttonText: banner.button_text || 'Get Started',
    buttonTo: banner.button_action_url || '/register',
    titleColor: banner.main_heading_text_color,
    descriptionColor: banner.sub_heading_text_color,
  };
}

function HeroCta({ slide }) {
  if (isExternalUrl(slide.buttonTo)) {
    return (
      <a href={slide.buttonTo} className="home-btn home-btn--primary">
        {slide.buttonText}
      </a>
    );
  }
  return <HomeBtn to={slide.buttonTo}>{slide.buttonText}</HomeBtn>;
}

function HeroSlide({ slide, reduce }) {
  return (
    <div className="home-container home-hero-grid">
      <motion.div
        className="home-hero-copy"
        initial={reduce ? false : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: HOME_EASE }}
      >
        {slide.title ? (
          <h2
            className="home-hero-title"
            style={slide.titleColor ? { color: slide.titleColor } : undefined}
          >
            {slide.title}
          </h2>
        ) : null}
        {slide.description ? (
          <p
            className="home-hero-sub"
            style={slide.descriptionColor ? { color: slide.descriptionColor } : undefined}
          >
            {slide.description}
          </p>
        ) : null}
        <div className="home-hero-actions">
          <HeroCta slide={slide} />
        </div>
      </motion.div>

      <div className="home-hero-visual">
        <div className="home-hero-stage">
          <picture>
            {slide.imageMobile ? (
              <source media="(max-width: 768px)" srcSet={slide.imageMobile} />
            ) : null}
            <img
              src={slide.image}
              alt={slide.alt}
              className="home-hero-photo"
              draggable="false"
            />
          </picture>
        </div>
      </div>
    </div>
  );
}

const HomeHero = () => {
  const reduce = useReducedMotion();
  const { banners, loading, hasDynamicBanner } = usePageBanners(HOME_BANNER_SECTION);

  const slides = hasDynamicBanner
    ? banners.filter((banner) => banner.imageDesktopUrl).map(mapBannerToSlide)
    : FALLBACK_SLIDES;

  if (loading) {
    return <section className="home-hero home-hero--loading" aria-busy="true" />;
  }

  if (slides.length === 0) {
    return null;
  }

  return (
    <section className="home-hero">
      <Swiper
        key={slides.map((slide) => slide.id).join('-')}
        modules={[Pagination, Autoplay]}
        pagination={slides.length > 1 ? { clickable: true } : false}
        loop={slides.length > 2}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={
          reduce || slides.length < 2
            ? false
            : { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }
        }
        className="home-hero-swiper"
        onSwiper={initBannerSwiper}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <HeroSlide slide={slide} reduce={reduce} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default HomeHero;
